import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import { useState } from 'react';
import { useUserManagerRole } from '@/modules/authentification/hooks';
import {
  BooleanFilter,
  getBooleanFilter,
} from '@/modules/common/components/table/filter/BooleanFilter';
import { AmountWithCurrency } from '@/modules/common/components/fragments/AmountText';
import classNames from 'classnames';
import SepaTransaction from '../../../../../app/spraypaint/sepaTransactions/SepaTransaction';
import {
  ButtonOnClickHandler,
  ButtonPrimary,
  ButtonSecondary,
} from '../../../../common/components/buttons';
import { useBankReconciliationTransition } from '../../hooks/useBankReconciliationTransition';
import {
  getDateFilterFn,
  DateFilter,
} from '../../../../common/components/table/filter/DateFilter';
import { CompletedValidationModal } from '../../bankAssociation/components/CompletedValidationModal';
import {
  getSupInfFilter,
  SupInfFilter,
} from '../../../../common/components/table/filter/SupInfFilter';
import { TableHeaderLabelTooltiped } from '../../../../common/components/tooltip/TableHeaderAmountTooltip';

const columnHelper = createColumnHelper<ModelRecord<SepaTransaction>>();

const commonColumns = () => {
  const { t } = useTranslation();
  return [
    columnHelper.accessor('legalEntity.name', {
      // filter working but return two lines: the chosen id and the next one
      header: t('front.sepa.show.table.legalEntity') as string,
      cell: (info) => (
        <div className="text-center">
          <p>{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('paymentDate', {
      // filter working but return two lines: the chosen id and the next one
      header: t('front.sepa.show.table.paymentDate') as string,
      filterFn: getDateFilterFn(),
      meta: {
        filter: {
          Component: DateFilter,
        },
      },
      cell: (info) => (
        <div className="text-center">
          <p>{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('direction', {
      // filter working
      header: t('front.sepa.show.table.direction') as string,
      cell: (info: any) => (
        <div className="text-center">
          <p>{info.getValue().toUpperCase()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('label', {
      header: t('front.sepa.show.table.label') as string,
      cell: (cell) => {
        const { original } = cell.row;
        console.log('original', original);

        return (
          <div className="text-left">
            <p>
              {original.label ||
                original.endToEndId ||
                original.additionalInformation}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor('debitorName', {
      // filter working
      header: t('front.sepa.show.table.debitorName') as string,
      cell: (info) => (
        <div className="text-left">
          <p>{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('customAmount', {
      id: 'amount',
      header: () => (
        <TableHeaderLabelTooltiped
          label={t('front.sepa.show.table.amount')}
          msg={t('front.table.header.amount.tooltipMsg')}
        />
      ),
      filterFn: getSupInfFilter(),
      meta: {
        filter: {
          Component: SupInfFilter,
        },
      },
      cell: (cell) => {
        const { direction, amount } = cell.row.original;
        const amountClassNames = classNames(
          'flex pl-7 pr-3 h-[100%] items-center justify-end',
          { 'text-color-txt-failure': direction === 'debit' },
        );
        return (
          <div className={amountClassNames}>
            <AmountWithCurrency
              amount={
                direction === 'credit'
                  ? amount?.amount || 0
                  : -(amount?.amount || 0)
              }
              currencyIsoCode={amount?.currency || 'EUR'}
            />
          </div>
        );
      },
    }),
  ];
};

export const awaitingBankReconciliationColumn = () => {
  const { t } = useTranslation();
  const isOnlyManager = useUserManagerRole();

  const ActionsCell = ({
    cell,
  }: CellContext<ModelRecord<SepaTransaction>, unknown>) => {
    const data = cell.row.original;
    const { mutate, isLoading } = useBankReconciliationTransition();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [comment, setComment] = useState('');

    const handleSetIsModalOpen: ButtonOnClickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsModalOpen((prev) => !prev);
    };

    const handleOnClick: ButtonOnClickHandler = (event) => {
      event.stopPropagation();
      event.preventDefault();
      mutate({
        resourceId: data.id,
        transitionName: 'orphan',
        transitionParams: { comment },
      });
    };

    const handleFill = (e: React.ChangeEvent<HTMLInputElement>) => {
      setComment(e.target.value);
    };

    return (
      <div className="flex justify-center items-center h-[100%]">
        <CompletedValidationModal
          handleSetCloseModal={handleSetIsModalOpen}
          isOpen={isModalOpen}
          handleOnClick={handleOnClick}
          isLoading={isLoading}
          handleFill={handleFill}
        />
        <ButtonPrimary onClick={handleSetIsModalOpen} disabled={isOnlyManager}>
          {t('default.button.renounce')}
        </ButtonPrimary>
      </div>
    );
  };

  return [
    ...commonColumns(),
    columnHelper.accessor('matchingCandidatesCount', {
      header: t('front.sepa.show.table.matchingCandidates') as string,
      filterFn: getSupInfFilter(),
      cell: (info) => (
        <div className="text-left">
          <p>{info.getValue() === 0 ? '-' : info.getValue()}</p>
        </div>
      ),
      meta: {
        filter: {
          Component: SupInfFilter,
        },
      },
    }),
    columnHelper.accessor('actions', {
      header: t('default.actions'),
      enableColumnFilter: false,
      enableSorting: false,
      cell: ActionsCell,
    }),
  ];
};

export const completedBankReconciliationColumn = () => {
  const { t } = useTranslation();
  const isOnlyManager = useUserManagerRole();

  const ActionsCell = ({
    cell,
  }: CellContext<ModelRecord<SepaTransaction>, unknown>) => {
    const data = cell.row.original;
    const { mutate } = useBankReconciliationTransition();

    const handleOnClick: ButtonOnClickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      mutate({
        resourceId: data.id,
        transitionName:
          data.status === 'orphaned' ? 'back_to_pending_validation' : 'cancel',
      });
    };

    return (
      <div className="flex justify-center items-center">
        <ButtonSecondary onClick={handleOnClick} disabled={isOnlyManager}>
          {t('default.button.cancel')}
        </ButtonSecondary>
      </div>
    );
  };

  return [
    ...commonColumns(),
    columnHelper.accessor('autoValidated', {
      header: 'Validation auto' as string,
      filterFn: getBooleanFilter(),
      meta: {
        filter: {
          Component: BooleanFilter,
        },
      },
      cell: (info) => (
        <div className="text-center">
          <p>
            {info.getValue()
              ? (t('default.yes') as string)
              : (t('default.no') as string)}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor('status', {
      header: t('default.status'),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('actions', {
      header: t('default.actions'),
      enableColumnFilter: false,
      enableSorting: false,
      cell: ActionsCell,
    }),
  ];
};
